export type NamedOption = {
  id: number;
  name: string;
};

export type SectorOption = {
  id: number;
  name: string;
  industry: string;
  subSector: string[];
};

export type CompanySizeOption = {
  id: number;
  value: string;
};

export type KpiDataRow = {
  id: number;
  function: string;
  kpiCategory: string;
  kpiName: string;
};

export const industry: NamedOption[] = [
  { id: 1, name: 'Consumer' },
  { id: 2, name: 'Energy, Resources & Industrials' },
  { id: 3, name: 'Financial Services' },
  { id: 4, name: 'Government & Public Services' },
  { id: 5, name: 'Life Sciences & Health Care' },
  { id: 6, name: 'Professional Services' },
  { id: 7, name: 'Technology, Media, and Telecom' },
];

export const sector: SectorOption[] = [
  { id: 1, name: 'Automotive', industry: 'Consumer', subSector: ['Automotive Suppliers', 'Original Equipment Manufacturers'] },
  { id: 2, name: 'Retail & Consumer Products', industry: 'Consumer', subSector: ['Agribusiness', 'Food, Beverage, Personal & Household', 'Grocery, Convenience, Drug & Pharmacy', 'Mass, Discount, Specialty Retail, & Apparel/Footwear', 'Wholesale Distribution'] },
  { id: 3, name: 'Transportation, Hospitality & Services', industry: 'Consumer', subSector: ['Airlines', 'Hospitality', 'Restaurant & Food Service', 'Transportation'] },
  { id: 4, name: 'Energy & Chemicals', industry: 'Energy, Resources & Industrials', subSector: ['Chemicals & Specialty Materials', 'Oil & Gas'] },
  { id: 5, name: 'Industrial Products & Construction', industry: 'Energy, Resources & Industrials', subSector: ['Aerospace & Defense', 'Engineering & Construction', 'Heavy Machinery & Equipment', 'Industrial Products / Industrial Conglomerates', 'Trading Houses'] },
  { id: 6, name: 'Power, Utilities & Renewables', industry: 'Energy, Resources & Industrials', subSector: ['Renewable Energy', 'Water & Waste Utilities'] },
  { id: 7, name: 'Banking & Capital Markets', industry: 'Financial Services', subSector: ['Capital Markets', 'Payments', 'Public Institutions', 'Retail Banking', 'Wealth Management'] },
  { id: 8, name: 'Insurance', industry: 'Financial Services', subSector: ['Brokers', 'General Insurance', 'Life and Savings', 'Reinsurance'] },
  { id: 9, name: 'Investment Management', industry: 'Financial Services', subSector: ['Private Equity, Sovereign Wealth Funds', 'Traditional Asset Managers'] },
  { id: 10, name: 'Real Estate', industry: 'Financial Services', subSector: ['Developers & Homebuilders', 'Principal & Investors & Private Equity Real Estate', 'Real Estate Services & Brokers', 'REITs & Owner-Operators or Properties'] },
  { id: 11, name: 'Civil Government', industry: 'Government & Public Services', subSector: ['Civil Government'] },
  { id: 12, name: 'Defense, Security & Justice', industry: 'Government & Public Services', subSector: ['Defense, Security & Justice'] },
  { id: 13, name: 'Federal Health', industry: 'Government & Public Services', subSector: ['Federal Health'] },
  { id: 14, name: 'State, Local & Higher Education', industry: 'Government & Public Services', subSector: ['State, Local & Higher Education'] },
  { id: 15, name: 'Life Sciences', industry: 'Life Sciences & Health Care', subSector: ['Biopharma', 'Generics & Biosimilars', 'Med Tech', 'Medical Wholesale & Distribution'] },
  { id: 16, name: 'Health Care', industry: 'Life Sciences & Health Care', subSector: ['Health Care Providers', 'Health Plans & Payers'] },
  { id: 17, name: 'Accounting & Consulting Services', industry: 'Professional Services', subSector: ['Audit and Tax Services', 'Audit, Tax, and Consulting Services', 'Consulting Services'] },
  { id: 18, name: 'Technology', industry: 'Technology, Media, and Telecom', subSector: ['Hardware', 'Semiconductors', 'Software & Services'] },
  { id: 19, name: 'Telecom, Media and Entertainment', industry: 'Technology, Media, and Telecom', subSector: ['Media & Entertainment', 'Telecom', 'Sports & Live Entertainment'] },
];

export const companySize: CompanySizeOption[] = [
  { id: 1, value: '<$50M' },
  { id: 2, value: '$50M – $100M' },
  { id: 3, value: '$100M – $250M' },
  { id: 4, value: '$250M – $500M' },
  { id: 5, value: '$500M – $1B' },
];

export const geography: NamedOption[] = [
  { id: 1, name: 'Global' },
  { id: 2, name: 'USA' },
];

export const ownerShipType: NamedOption[] = [
  { id: 1, name: 'Private' },
  { id: 2, name: 'Public' },
  { id: 3, name: 'Private (PE / VC backed)' },
];

export const kpiCategory: NamedOption[] = [
  { id: 1, name: 'Cost Structure' },
  { id: 2, name: 'Workforce Cost Efficiency' },
  { id: 3, name: 'Workforce Productivity' },
  { id: 4, name: 'Profitability & Growth' },
  { id: 5, name: 'Workforce Composition' },
];

export const KpiList: NamedOption[] = [
  { id: 1, name: 'Audit & Tax Spend as % of Total Finance Spend' },
  { id: 2, name: 'IT Headcount Spend as % of IT Spend' },
  { id: 3, name: 'HR Operations FTEs per $1B Revenue' },
  { id: 4, name: 'EBITDA margin' },
  { id: 5, name: '% of Finance Work Offshored/Shared Services' },
];

export const functionList: NamedOption[] = [
  { id: 1, name: 'Finance' },
  { id: 2, name: 'COGS' },
  { id: 3, name: 'Executive & Admin' },
  { id: 4, name: 'Facilities' },
  { id: 5, name: 'HR' },
  { id: 6, name: 'Legal & Compliance' },
  { id: 7, name: 'IT' },
  { id: 8, name: 'Quality & Risk' },
  { id: 9, name: 'Operations' },
  { id: 10, name: 'R&D' },
  { id: 11, name: 'Sales' },
];

export const kpiData: KpiDataRow[] = [
  { id: 1, function: 'Finance', kpiCategory: 'Cost Structure', kpiName: 'Audit & Tax Spend as % of Total Finance Spend' },
  { id: 2, function: 'COGS', kpiCategory: 'Cost Structure', kpiName: 'COGS Spend as % of Revenue' },
  { id: 3, function: 'Executive & Admin', kpiCategory: 'Cost Structure', kpiName: 'Executive & Admin Spend as % of Revenue' },
  { id: 4, function: 'Facilities', kpiCategory: 'Cost Structure', kpiName: 'Facilities Spend as % of Revenue' },
  { id: 5, function: 'Facilities', kpiCategory: 'Cost Structure', kpiName: 'Facility Non-HC Cost per FTE' },
  { id: 6, function: 'Finance', kpiCategory: 'Cost Structure', kpiName: 'Finance Non-Headcount Spend as % of IT Spend' },
  { id: 7, function: 'Finance', kpiCategory: 'Cost Structure', kpiName: 'Finance Spend as % of Revenue' },
  { id: 8, function: 'HR', kpiCategory: 'Cost Structure', kpiName: 'HR Non-Headcount Spend as % of IT Spend' },
  { id: 9, function: 'HR', kpiCategory: 'Cost Structure', kpiName: 'HR Spend as % of Revenue' },
  { id: 10, function: 'IT', kpiCategory: 'Cost Structure', kpiName: 'IT Headcount Spend as % of IT Spend' },
  { id: 11, function: 'IT', kpiCategory: 'Cost Structure', kpiName: 'IT Non-Headcount Spend as % of IT Spend' },
  { id: 12, function: 'IT', kpiCategory: 'Cost Structure', kpiName: 'IT Non-Headcount Spend per FTE' },
  { id: 13, function: 'IT', kpiCategory: 'Cost Structure', kpiName: 'IT Spend as % of Revenue' },
  { id: 14, function: 'Legal', kpiCategory: 'Cost Structure', kpiName: 'Legal Spend as % of Revenue' },
  { id: 15, function: 'Marketing', kpiCategory: 'Cost Structure', kpiName: 'Marketing Non-Headcount Spend as % of IT Spend' },
  { id: 16, function: 'Marketing', kpiCategory: 'Cost Structure', kpiName: 'Marketing Spend as % of Revenue' },
  { id: 17, function: 'HR', kpiCategory: 'Cost Structure', kpiName: 'Payroll Processing Spend as % of Total HR Spend' },
  { id: 18, function: 'R&D', kpiCategory: 'Cost Structure', kpiName: 'R&D Non-Headcount Spend as % of IT Spend' },
  { id: 19, function: 'R&D', kpiCategory: 'Cost Structure', kpiName: 'R&D Spend as % of Revenue' },
  { id: 20, function: 'HR', kpiCategory: 'Cost Structure', kpiName: 'Recruitment Spend as % of Total HR Spend' },
  { id: 21, function: 'Sales', kpiCategory: 'Cost Structure', kpiName: 'Sales Non-Headcount Spend as % of IT Spend' },
  { id: 22, function: 'Sales', kpiCategory: 'Cost Structure', kpiName: 'Sales Spend as % of Revenue' },
  { id: 23, function: 'Finance', kpiCategory: 'Cost Structure', kpiName: 'Total Insurance Spend as % of Revenue' },
  { id: 24, function: 'Overall', kpiCategory: 'Cost Structure', kpiName: 'Total Labor Spend as % of Revenue' },
  { id: 25, function: 'HR', kpiCategory: 'Cost Structure', kpiName: 'Training Spend as % of Total HR Spend' },
  { id: 26, function: 'IT', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for IT FTEs' },
  { id: 27, function: 'Finance', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for Finance FTEs' },
  { id: 28, function: 'R&D', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for R&D FTEs' },
  { id: 29, function: 'HR', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for HR FTEs' },
  { id: 30, function: 'Sales', kpiCategory: 'Workforce Productivity', kpiName: 'Sales FTEs per $1B Revenue' },
  { id: 31, function: 'COGS', kpiCategory: 'Workforce Productivity', kpiName: 'COGS FTEs per $1B Revenue' },
  { id: 32, function: 'Marketing', kpiCategory: 'Workforce Productivity', kpiName: 'Marketing FTEs per $1B Revenue' },
  { id: 33, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT FTEs per $1B Revenue' },
  { id: 34, function: 'Finance', kpiCategory: 'Workforce Productivity', kpiName: 'Finance FTEs per $1B Revenue' },
  { id: 35, function: 'R&D', kpiCategory: 'Workforce Productivity', kpiName: 'R&D FTEs per $1B Revenue' },
  { id: 36, function: 'HR', kpiCategory: 'Workforce Productivity', kpiName: 'HR FTEs per $1B Revenue' },
  { id: 37, function: 'Facilities', kpiCategory: 'Workforce Productivity', kpiName: 'Facilities FTEs per $1B Revenue' },
  { id: 38, function: 'Legal', kpiCategory: 'Workforce Productivity', kpiName: 'Legal FTEs per $1B Revenue' },
  { id: 39, function: 'Overall', kpiCategory: 'Workforce Productivity', kpiName: 'Total company offshore %' },
  { id: 40, function: 'R&D', kpiCategory: 'Workforce Productivity', kpiName: 'Total R&D offshore %' },
  { id: 41, function: 'Overall', kpiCategory: 'Workforce Productivity', kpiName: 'Total G&A offshore %' },
  { id: 42, function: 'Executive & Admin', kpiCategory: 'Workforce Productivity', kpiName: 'Executive & Admin FTEs per $1B Revenue' },
  { id: 43, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT Cloud & Hosting FTEs per $1B Revenue' },
  { id: 44, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT Equipment, Networking & Infrastructure FTEs per $1B Revenue' },
  { id: 45, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT Systems & Operations Management FTEs per $1B Revenue' },
  { id: 46, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT Security FTEs per $1B Revenue' },
  { id: 47, function: 'IT', kpiCategory: 'Workforce Productivity', kpiName: 'IT Support & Helpdesk FTEs per $1B Revenue' },
  { id: 48, function: 'Finance', kpiCategory: 'Workforce Productivity', kpiName: 'Accounts Receivable FTEs per $1B Revenue' },
  { id: 49, function: 'Finance', kpiCategory: 'Workforce Productivity', kpiName: 'Accounts Payable FTEs per $1B Revenue' },
  { id: 50, function: 'Finance', kpiCategory: 'Workforce Productivity', kpiName: 'Audit & Tax FTEs per $1B Revenue' },
  { id: 51, function: 'Finance', kpiCategory: 'Workforce Productivity', kpiName: 'Treasury FTEs per $1B Revenue' },
  { id: 52, function: 'HR', kpiCategory: 'Workforce Productivity', kpiName: 'Payroll Processing FTEs per $1B Revenue' },
  { id: 53, function: 'HR', kpiCategory: 'Workforce Productivity', kpiName: 'HRBP FTEs per $1B Revenue' },
  { id: 54, function: 'HR', kpiCategory: 'Workforce Productivity', kpiName: 'HR Operations FTEs per $1B Revenue' },
  { id: 55, function: 'HR', kpiCategory: 'Workforce Productivity', kpiName: 'Talent Acquisition FTEs per $1B Revenue' },
  { id: 56, function: 'Overall', kpiCategory: 'Profitability & Growth', kpiName: 'Gross Margin (%)' },
  { id: 57, function: 'Overall', kpiCategory: 'Profitability & Growth', kpiName: 'EBITDA margin' },
  { id: 58, function: 'Overall', kpiCategory: 'Profitability & Growth', kpiName: 'Rule of 40' },
  { id: 59, function: 'Overall', kpiCategory: 'Profitability & Growth', kpiName: 'Growth Rate' },
  { id: 60, function: 'IT', kpiCategory: 'Workforce Composition', kpiName: 'IT FTEs as % of Total Employees' },
  { id: 61, function: 'Finance', kpiCategory: 'Workforce Composition', kpiName: 'Finance Outsourcing Cost as % of Revenue' },
  { id: 62, function: 'IT', kpiCategory: 'Workforce Composition', kpiName: 'IT Outsourcing Cost as % of Revenue' },
  { id: 63, function: 'HR', kpiCategory: 'Workforce Composition', kpiName: 'HR Outsourcing Cost as % of Revenue' },
  { id: 64, function: 'R&D', kpiCategory: 'Workforce Composition', kpiName: 'R&D Outsourcing Cost as % of Revenue' },
  { id: 65, function: 'Sales', kpiCategory: 'Workforce Composition', kpiName: 'Sales Outsourcing Cost as % of Revenue' },
  { id: 66, function: 'HR', kpiCategory: 'Workforce Composition', kpiName: '% of HR Work Offshored/Shared Services' },
  { id: 67, function: 'Finance', kpiCategory: 'Workforce Composition', kpiName: '% of Finance Work Offshored/Shared Services' },
  { id: 68, function: 'IT', kpiCategory: 'Workforce Composition', kpiName: '% of IT Work Offshored/Shared Services' },
  { id: 69, function: 'R&D', kpiCategory: 'Workforce Composition', kpiName: '% of R&D Work Offshored/Shared Services' },
  { id: 70, function: 'Sales', kpiCategory: 'Workforce Composition', kpiName: '% of Sales Work Offshored/Shared Services' },
  { id: 71, function: 'Overall', kpiCategory: 'Workforce Composition', kpiName: 'Geographic File Mix (% in Low/High Cost Countries)' },
  { id: 72, function: 'Sales', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for Sales FTEs' },
  { id: 73, function: 'Marketing', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for Marketing FTEs' },
  { id: 74, function: 'Overall', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Fringe & Benefits as % of Base Compensation' },
  { id: 75, function: 'Finance', kpiCategory: 'Workforce Composition', kpiName: 'Accounts Payable FTEs as % of Finance FTEs' },
  { id: 76, function: 'Legal & Compliance', kpiCategory: 'Workforce Cost Efficiency', kpiName: 'Average Fully Loaded Cost (FLC) for Legal & Compliance FTEs' },
];